import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { Element } from '@phrasee/phrasee-typings'
import { useQueryClient } from '@tanstack/react-query'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { errorToast } from 'common/components/toastNotification'
import { useAppSelector } from 'common/hooks/redux'
import useWebsockets, { WsResponseBase } from 'common/hooks/useWebsockets'
import useGetMergeTagsQuery from 'features/admin/accountSettings/api/queries/useGetMergeTagsQuery'
import useGenerateBriefFromContentMutation from 'features/unifiedFlow/api/mutations/useGenerateBriefFromContentMutation'
import useGenerateCustomBriefFromContentMutation from 'features/unifiedFlow/api/mutations/useGenerateCustomBriefFromContentMutation'
import { ContentWithProgress } from 'features/unifiedFlow/api/queries/useGetContentQuery'
import { contentKeys } from 'features/unifiedFlow/api/queryKeys'

import useWebsocketMessage from '../../../hooks/websocketMessage'
import { getWsTopic } from '../../../hooks/websocketMessage/helpers'

export type GenerateBriefFromContentOptions = {
  brandVoiceId?: string
  mergeTagIds?: string[]
}

export const useBriefFromContentGeneration = ({
  content,
  selectedElement,
  isGlobalSettings,
  isOptimized,
  hasVariants,
  isModalOpenRef,
  setResetWarningModalState,
}: {
  content: ContentWithProgress | undefined
  selectedElement: Element | undefined
  isGlobalSettings: boolean
  isOptimized: boolean
  hasVariants: boolean | undefined
  isModalOpenRef: React.MutableRefObject<boolean>
  setResetWarningModalState: Dispatch<
    SetStateAction<{
      isOpen: boolean
      onConfirm?: () => void
    }>
  >
}) => {
  const flags = useFlags()
  const queryClient = useQueryClient()
  const { sendWebsocketMessage } = useWebsocketMessage()
  const accountId = useAppSelector((state) => state.authStates.accountId)
  const { data: mergeTags } = useGetMergeTagsQuery()
  const {
    lastJsonMessage,
  }: {
    lastJsonMessage: WsResponseBase | null
  } = useWebsockets()

  const generateBriefFromContentMutation = useGenerateBriefFromContentMutation()
  const generateCustomBriefFromContentMutation =
    useGenerateCustomBriefFromContentMutation()

  // Tracks the full async duration (POST + websocket round-trip) scoped
  // specifically to this generation, rather than any content-wide websocket
  // activity — otherwise an unrelated in-flight action (e.g. variants
  // generation) makes this show as "generating" too.
  const currentGeneratedBrief =
    !isGlobalSettings && selectedElement
      ? selectedElement.user_input?.generated_brief
      : content?.user_input.generated_brief
  const [isAwaitingResult, setIsAwaitingResult] = useState(false)
  const previousGeneratedBriefRef = useRef(currentGeneratedBrief)
  // The websocket topic this generation is waiting on, so a failure for an
  // unrelated in-flight action can't release our awaiting state (and vice
  // versa). Cleared whenever we stop awaiting, by success or by failure.
  const awaitedWsTopicRef = useRef<string | undefined>(undefined)

  const stopAwaitingResult = () => {
    awaitedWsTopicRef.current = undefined
    setIsAwaitingResult(false)
  }

  useEffect(() => {
    if (
      isAwaitingResult &&
      currentGeneratedBrief &&
      currentGeneratedBrief !== previousGeneratedBriefRef.current
    ) {
      stopAwaitingResult()
    }
    previousGeneratedBriefRef.current = currentGeneratedBrief
  }, [currentGeneratedBrief, isAwaitingResult])

  // A `status: 'error'` websocket message is the other way a generation ends.
  // Without this the skeleton on the brief description never clears: the error
  // path leaves `generated_brief` untouched, so the success effect above can
  // never fire and the field stays disabled until a page reload.
  useEffect(() => {
    const awaitedWsTopic = awaitedWsTopicRef.current

    if (
      !awaitedWsTopic ||
      lastJsonMessage?.status !== 'error' ||
      !lastJsonMessage.topics?.includes(awaitedWsTopic)
    ) {
      return
    }

    // Deliberately no toast here — useWebsocketFullScreenLoader already
    // surfaces `errorMessage` for these same topics, so toasting again would
    // report one failure twice.
    stopAwaitingResult()
    // Respond only to new websocket messages
  }, [lastJsonMessage])

  // The content-api brief-from-content endpoints don't persist a "remove"
  // action yet, so this just clears the local marker — a fresh import simply
  // overwrites `raw_content`/`raw_content_type` server-side. Brief description
  // edits no longer clear provenance; only this explicit remove does (locally).
  //
  // `revertedDescription` is reverted in the cached `user_input_form` alongside
  // the marker. Reverting the form field alone isn't enough: BriefPage rebuilds
  // `formInitialValues` from this cache, and react-final-form re-initializes on
  // any shallow-unequal `initialValues`. So an unrelated late arrival — most
  // commonly the brand voices query still in flight on a fresh page load —
  // recomputes that memo and re-initializes the form, resurrecting the removed
  // reference's summary in the description box while the chip stays gone.
  const clearRawContent = (revertedDescription?: string) => {
    if (!content) {
      return
    }

    const elementId =
      !isGlobalSettings && selectedElement?.element_id
        ? selectedElement.element_id
        : undefined

    queryClient.setQueryData(
      contentKeys.content(content._id),
      (old: ContentWithProgress | undefined) => {
        if (!old) {
          return old
        }

        const withoutRawContent = (
          userInput: NonNullable<(typeof old.elements)[number]['user_input']>
        ) =>
          ({
            ...userInput,
            raw_content: undefined,
            raw_content_type: undefined,
            ...(revertedDescription !== undefined && {
              user_input_form: userInput.user_input_form?.map((field) =>
                field.name === 'description_textarea'
                  ? { ...field, value: revertedDescription }
                  : field
              ),
            }),
          }) as typeof userInput

        if (elementId != null) {
          return {
            ...old,
            elements: old.elements.map((element) =>
              element.element_id === elementId && element.user_input
                ? {
                    ...element,
                    user_input: withoutRawContent(element.user_input),
                  }
                : element
            ),
          }
        }

        return {
          ...old,
          user_input: withoutRawContent(old.user_input),
        }
      }
    )
  }

  // A rejected POST means no websocket message is ever coming, so nothing else
  // can release the awaiting state. Callers pair this with an `unsubscribe` for
  // their own topic, which also drops the optimistic `ws_messages` entry that
  // would otherwise keep the full-screen loader up.
  const handleGenerationRequestFailure = () => {
    stopAwaitingResult()
    errorToast(
      'Something went wrong summarising your reference. Please try again.'
    )
  }

  const generateBriefFromContent = (
    contentToGenerateFrom: string,
    contentType: 'html' | 'text' | 'image',
    options?: GenerateBriefFromContentOptions
  ) => {
    if (!content) {
      return
    }

    if (hasVariants && isGlobalSettings && !isModalOpenRef.current) {
      setResetWarningModalState({
        isOpen: true,
        onConfirm: () =>
          generateBriefFromContent(contentToGenerateFrom, contentType, options),
      })
      return
    }

    const selectedMergeTags =
      mergeTags?.filter((tag) => options?.mergeTagIds?.includes(tag.id)) || []

    setIsAwaitingResult(true)

    if (!isGlobalSettings && selectedElement?.element_id) {
      const contentId = content._id
      const elementId = selectedElement.element_id
      const wsTopic = getWsTopic({
        data: { contentId, elementId },
        action: 'generateElementBrief',
      })

      sendWebsocketMessage({
        action: 'generateElementBrief',
        data: { contentId, elementId },
        subscriptionAction: 'subscribe',
      })

      awaitedWsTopicRef.current = wsTopic

      generateCustomBriefFromContentMutation.mutate(
        {
          accountId,
          contentId,
          elementId,
          content: contentToGenerateFrom,
          contentType,
          wsTopic,
          ...(!isOptimized && { brandVoiceId: options?.brandVoiceId }),
          ...(flags.showMergeTags && { mergeTags: selectedMergeTags }),
        },
        {
          onError: () => {
            handleGenerationRequestFailure()
            sendWebsocketMessage({
              action: 'generateElementBrief',
              data: { contentId, elementId },
              subscriptionAction: 'unsubscribe',
            })
          },
        }
      )
    } else {
      const contentId = content._id
      const wsTopic = getWsTopic({
        data: { contentId },
        action: 'generateGlobalBrief',
      })

      sendWebsocketMessage({
        action: 'generateGlobalBrief',
        data: { contentId },
        subscriptionAction: 'subscribe',
      })

      awaitedWsTopicRef.current = wsTopic

      generateBriefFromContentMutation.mutate(
        {
          accountId,
          contentId,
          content: contentToGenerateFrom,
          contentType,
          wsTopic,
          ...(!isOptimized && { brandVoiceId: options?.brandVoiceId }),
          ...(flags.showMergeTags && { mergeTags: selectedMergeTags }),
        },
        {
          onError: () => {
            handleGenerationRequestFailure()
            sendWebsocketMessage({
              action: 'generateGlobalBrief',
              data: { contentId },
              subscriptionAction: 'unsubscribe',
            })
          },
        }
      )
    }
  }

  const isGeneratingBriefFromContent =
    generateBriefFromContentMutation.isPending ||
    generateCustomBriefFromContentMutation.isPending ||
    isAwaitingResult

  return {
    generateBriefFromContent,
    clearRawContent,
    isGeneratingBriefFromContent,
  }
}
